import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  CheckOutlined,
  CopyOutlined,
  KeyOutlined,
  StopOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  inviteUser,
  listUsers,
  replaceUserRoles,
  setUserEnabled,
  setUserSpoke,
} from '@/api/uam';
import { listSpokes } from '@/api/tenantAdmin';
import { listRoles, type CustomRole } from '@/api/teamAccess';
import { isNotAvailable, normalizeApiError, type NormalizedApiError } from '@/api/errors';
import type { Spoke, UamInviteView, UamUserView } from '@/api/types';
import { useAuth } from '@/auth/OlAuthContext';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';
import {
  ASSIGNABLE_ROLES,
  CATALOGUE_ROLE_NAMES,
  isCustomRoleName,
  ROLE_LABEL,
  type RoleMeta,
} from './roleCatalogue';
import { getPermissionMatrix } from './permissionMatrix';
import { UserSessionsModal } from './UserSessionsModal';

const { Title, Paragraph, Text } = Typography;

const PAGE_SIZE = 25;

/**
 * Roles rendered as tags — catalogue label where known, raw token otherwise (never invent a name).
 * Custom `x_` roles render DISTINCTLY (bordered, monospace) so tenant-authored authority is never
 * mistaken for the fixed catalogue.
 */
function RoleTags({ roles }: { roles: string[] }) {
  if (!roles.length) return <Text type="secondary">—</Text>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {roles.map((r) => {
        const platform = r === 'platform_admin';
        const custom = isCustomRoleName(r);
        return (
          <Tag
            key={r}
            bordered={custom}
            color={custom ? 'purple' : undefined}
            title={custom ? `${r} — tenant-authored custom role` : r}
            className={custom ? 'ol-mono' : undefined}
            style={{ margin: 0, fontSize: 11, ...(platform ? { opacity: 0.75 } : {}) }}
          >
            {ROLE_LABEL.get(r) ?? r}
          </Tag>
        );
      })}
    </span>
  );
}

/**
 * Does a role carry any MUTATING key? The exact server predicate (UamService.carriesMutatingKey):
 * every read-only key ends `.read`, so the suffix IS the predicate — over the bundled matrix for
 * catalogue roles, over the bound key list for custom roles. Used only to WARN proactively about
 * auditor exclusivity; the server refusal (UAM_AUDITOR_EXCLUSIVE) stays the gate.
 */
function carriesMutatingKey(role: string, customRoles: CustomRole[]): boolean {
  if (isCustomRoleName(role)) {
    const bound = customRoles.find((c) => c.roleKey === role)?.permissionKeys ?? [];
    return bound.some((k) => !k.endsWith('.read'));
  }
  try {
    return getPermissionMatrix().keys.some((k) => !k.key.endsWith('.read') && k.roles.includes(role));
  } catch {
    return false; // matrix unreadable → no client-side warning; the server still enforces
  }
}

/** Enabled state with BOTH colour and glyph (colour is never the sole signal — tokens.ts §14). */
function EnabledTag({ enabled }: { enabled: boolean }) {
  const color = enabled ? 'var(--ol-positive)' : 'var(--ol-text-tertiary)';
  const Icon = enabled ? CheckCircleFilled : StopOutlined;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color, fontSize: 12, fontWeight: 600 }}>
      <Icon aria-hidden />
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}

/** A full name from first/last, or em-dash when the directory has neither. */
function fullName(u: UamUserView): string | null {
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return n || null;
}

/** Options for a spoke <Select>, ensuring `current` (a possibly-unlisted binding) stays selectable. */
function spokeOptions(spokes: Spoke[], current?: string | null) {
  const opts = spokes.map((s) => ({
    value: s.spokeId,
    label: s.displayName ? `${s.displayName} (${s.spokeId})` : s.spokeId,
  }));
  if (current && !spokes.some((s) => s.spokeId === current)) {
    opts.unshift({ value: current, label: current });
  }
  return opts;
}

// ---------------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------------

/**
 * B1 · IAM/UAM — the hub-realm user directory (Keycloak read-through). List + invite (with a
 * one-time temp-password reveal) + role/spoke/enable editors. The server holds the real gates
 * (hub_admin writes; auditor reads) and the self/platform_admin guards — the panel mirrors them as
 * disabled actions for good UX, and maps every server ApiError.code to an inline, actionable message.
 */
export function UsersPanel() {
  const { message, modal } = App.useApp();
  const { user: me } = useAuth();
  const selfUserId = me?.sub;

  const [users, setUsers] = useState<UamUserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [first, setFirst] = useState(0);

  const [spokes, setSpokes] = useState<Spoke[]>([]);
  const [spokesUnavailable, setSpokesUnavailable] = useState(false);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [tempReveal, setTempReveal] = useState<UamInviteView | null>(null);
  const [rolesTarget, setRolesTarget] = useState<UamUserView | null>(null);
  const [spokeTarget, setSpokeTarget] = useState<UamUserView | null>(null);
  const [sessionsTarget, setSessionsTarget] = useState<UamUserView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      const page = await listUsers(first, PAGE_SIZE);
      setUsers(page.items);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // message stable; PAGE_SIZE const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first]);

  useEffect(() => {
    void load();
  }, [load]);

  // Spokes power the invite + change-spoke selects. Best-effort: a 404/unavailable just means the
  // spoke picker offers only "hub scope" — spoke binding is optional, so this is never fatal here.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await listSpokes();
        if (alive) setSpokes(res.spokes);
      } catch {
        if (alive) setSpokesUnavailable(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ACTIVE custom roles (UC2 §5) are assignable via UAM alongside the catalogue. Best-effort:
  // roles.read may 403/404 for this caller — the seven catalogue roles are still offered.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const roles = await listRoles('ACTIVE');
        if (alive) setCustomRoles(roles);
      } catch {
        /* degrade: catalogue-only choices */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** Replace one row in place after a mutation returns the fresh view. */
  const applyUser = useCallback((u: UamUserView) => {
    setUsers((prev) => prev.map((x) => (x.userId === u.userId ? u : x)));
  }, []);

  const handleCreated = useCallback(
    (view: UamInviteView) => {
      setInviteOpen(false);
      setTempReveal(view); // the ONE-TIME reveal — never a toast/log
      void load();
    },
    [load],
  );

  const toggleEnabled = useCallback(
    (u: UamUserView) => {
      const next = !u.enabled;
      modal.confirm({
        title: next ? `Enable ${u.username}?` : `Disable ${u.username}?`,
        icon: null,
        content: next
          ? 'The user will be able to sign in again immediately.'
          : 'Disabling blocks new sign-ins immediately. Existing access tokens age out at their (short) lifespan. You can re-enable at any time.',
        okText: next ? 'Enable' : 'Disable',
        okButtonProps: next ? undefined : { danger: true },
        onOk: async () => {
          try {
            const view = await setUserEnabled(u.userId, { enabled: next });
            applyUser(view);
            message.success(`${u.username} ${next ? 'enabled' : 'disabled'}.`);
          } catch (e) {
            const n = normalizeApiError(e);
            if (n.code === 'UAM_SELF_FORBIDDEN') {
              message.error('You can’t change your own account status.');
            } else if (n.code === 'LAST_HUB_ADMIN_PROTECTED') {
              message.error(
                `${u.username} is the last enabled hub admin — the server refuses to disable them ` +
                  'so the hub can never lock itself out of administration. Promote or enable another ' +
                  'hub admin first.',
              );
            } else if (isNotAvailable(n)) {
              message.info('The user-management API is not live yet (Track A pending).');
            } else {
              message.error(n.message);
            }
            throw e; // keep the confirm open on failure
          }
        },
      });
    },
    [applyUser, message, modal],
  );

  const columns: ColumnsType<UamUserView> = useMemo(
    () => [
      {
        title: 'User',
        dataIndex: 'username',
        key: 'username',
        render: (_, r) => {
          const name = fullName(r);
          return (
            <div style={{ minWidth: 160 }}>
              <div style={{ fontWeight: 600 }}>{r.username}</div>
              {name && <Text style={{ fontSize: 12, color: 'var(--ol-text-secondary)' }}>{name}</Text>}
            </div>
          );
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (v?: string | null) =>
          v ? <Text style={{ fontSize: 12.5 }}>{v}</Text> : <Text type="secondary">—</Text>,
      },
      {
        title: 'Roles',
        dataIndex: 'roles',
        key: 'roles',
        render: (roles: string[]) => <RoleTags roles={roles} />,
      },
      {
        title: 'Spoke',
        dataIndex: 'spokeId',
        key: 'spokeId',
        width: 150,
        render: (v?: string | null) =>
          v ? (
            <span className="ol-mono" style={{ fontSize: 11.5 }}>
              {v}
            </span>
          ) : (
            <Tooltip title="Hub scope — not bound to a single spoke">
              <Text type="secondary">—</Text>
            </Tooltip>
          ),
      },
      {
        title: 'Status',
        dataIndex: 'enabled',
        key: 'enabled',
        width: 120,
        render: (enabled: boolean) => <EnabledTag enabled={enabled} />,
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        render: (v?: string | null) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatAbsolute(v)}
          </Text>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 220,
        render: (_, r) => {
          const isSelf = !!selfUserId && r.userId === selfUserId;
          const isPlatform = r.roles.includes('platform_admin');
          const selfReason = 'You can’t change your own roles, spoke, or status.';
          const platformReason = 'This user holds platform_admin — manage them on the platform plane.';
          const rolesDisabled = isSelf || isPlatform;
          return (
            <Space size={2} wrap>
              <RowAction
                label="Roles"
                disabled={rolesDisabled}
                reason={isSelf ? selfReason : isPlatform ? platformReason : undefined}
                onClick={() => setRolesTarget(r)}
              />
              <RowAction
                label="Spoke"
                disabled={isSelf}
                reason={isSelf ? selfReason : undefined}
                onClick={() => setSpokeTarget(r)}
              />
              {/* Sessions is NOT self-disabled: revoking your own sessions is a logout, not an
                  escalation — the modal spells out that it signs you out of this console too. */}
              <RowAction label="Sessions" onClick={() => setSessionsTarget(r)} />
              <RowAction
                label={r.enabled ? 'Disable' : 'Enable'}
                danger={r.enabled}
                disabled={isSelf}
                reason={isSelf ? selfReason : undefined}
                onClick={() => toggleEnabled(r)}
              />
            </Space>
          );
        },
      },
    ],
    [selfUserId, toggleEnabled],
  );

  const inviteButton = (
    <Button type="primary" icon={<UserAddOutlined />} onClick={() => setInviteOpen(true)}>
      Invite user
    </Button>
  );

  const hasPrev = first > 0;
  const hasNext = users.length === PAGE_SIZE;
  const rangeLabel =
    users.length === 0 ? 'No users on this page' : `Showing ${first + 1}–${first + users.length}`;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Title level={5} style={{ margin: 0 }}>
            Users
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', maxWidth: 760, fontSize: 13 }}>
            The people in this hub. Invite a teammate, assign catalogue or custom roles, bind them to a
            spoke, or disable access. Changes are enforced server-side by the permission matrix — the
            console just makes them legible. Three guardrails always apply: nobody edits their own
            account, the last enabled hub admin can never be demoted or disabled, and an auditor may
            hold no role that mutates (see the Permission matrix tab).
          </Paragraph>
        </div>
        {!unavailable && inviteButton}
      </div>

      {unavailable ? (
        <EmptyState
          icon={<TeamOutlined />}
          title="User management"
          description="Your hub's users and self-serve invitations land here once the UAM API is live (Track A, IAM build). The directory is a read-through over your hub's Keycloak — there is no separate user store to keep in sync."
          bullets={[
            'A username-ordered directory: roles, spoke binding, enabled state',
            'Invite a user with catalogue roles and an optional spoke — a one-time temporary password is shown once',
            'Edit roles (replace-set), rebind or clear the spoke, disable/enable access',
          ]}
          action={inviteButton}
        />
      ) : (
        <>
          <Table<UamUserView>
            rowKey="userId"
            loading={loading}
            columns={columns}
            dataSource={users}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No users on this page.' }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              {rangeLabel}
            </Text>
            <Space size={4}>
              <Button
                size="small"
                disabled={!hasPrev || loading}
                onClick={() => setFirst((f) => Math.max(0, f - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="small"
                disabled={!hasNext || loading}
                onClick={() => setFirst((f) => f + PAGE_SIZE)}
              >
                Next
              </Button>
            </Space>
          </div>
        </>
      )}

      <InviteModal
        open={inviteOpen}
        spokes={spokes}
        spokesUnavailable={spokesUnavailable}
        customRoles={customRoles}
        onClose={() => setInviteOpen(false)}
        onCreated={handleCreated}
      />
      {tempReveal && <TempPasswordModal view={tempReveal} onClose={() => setTempReveal(null)} />}
      {rolesTarget && (
        <EditRolesModal
          target={rolesTarget}
          customRoles={customRoles}
          onClose={() => setRolesTarget(null)}
          onUpdated={(u) => {
            applyUser(u);
            setRolesTarget(null);
          }}
        />
      )}
      {spokeTarget && (
        <ChangeSpokeModal
          target={spokeTarget}
          spokes={spokes}
          spokesUnavailable={spokesUnavailable}
          onClose={() => setSpokeTarget(null)}
          onUpdated={(u) => {
            applyUser(u);
            setSpokeTarget(null);
          }}
        />
      )}
      {sessionsTarget && (
        <UserSessionsModal
          target={sessionsTarget}
          selfUserId={selfUserId}
          onClose={() => setSessionsTarget(null)}
        />
      )}
    </div>
  );
}

/** A row action button; when disabled it still explains itself (tooltip works over the wrapping span). */
function RowAction({
  label,
  danger,
  disabled,
  reason,
  onClick,
}: {
  label: string;
  danger?: boolean;
  disabled?: boolean;
  reason?: string;
  onClick: () => void;
}) {
  const btn = (
    <Button type="link" size="small" danger={danger} disabled={disabled} onClick={onClick} style={{ padding: '0 6px' }}>
      {label}
    </Button>
  );
  if (disabled && reason) {
    return (
      <Tooltip title={reason}>
        <span>{btn}</span>
      </Tooltip>
    );
  }
  return btn;
}

// ---------------------------------------------------------------------------------------------
// Role choices — shared by invite + edit. Catalogue roles first, then the hub's ACTIVE custom
// x_ roles rendered DISTINCTLY, plus a proactive auditor-exclusivity warning (the server refusal
// UAM_AUDITOR_EXCLUSIVE remains the gate — this only explains it before the round-trip).
// ---------------------------------------------------------------------------------------------

/** One checkbox row (catalogue or custom or retained-unknown). */
function RoleOption({ meta, custom }: { meta: RoleMeta; custom?: boolean }) {
  return (
    <Checkbox key={meta.role} value={meta.role}>
      <span style={{ fontWeight: 600 }}>{meta.label}</span>
      <span
        className="ol-mono"
        style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', marginLeft: 6 }}
      >
        {meta.role}
      </span>
      {custom && (
        <Tag color="purple" bordered style={{ fontSize: 9.5, marginLeft: 6, lineHeight: '14px' }}>
          custom
        </Tag>
      )}
      <div style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)' }}>{meta.blurb}</div>
    </Checkbox>
  );
}

/** Auditor-exclusivity heads-up for a candidate role set (mirrors the server predicate exactly). */
function AuditorExclusivityHint({ roles, customRoles }: { roles: string[]; customRoles: CustomRole[] }) {
  if (!roles.includes('auditor')) return null;
  const clashing = roles.filter((r) => r !== 'auditor' && carriesMutatingKey(r, customRoles));
  if (!clashing.length) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginTop: 10 }}
      message="Auditor is exclusive"
      description={`Auditor cannot be combined with roles that mutate (${clashing.join(', ')}). The server will refuse this set (UAM_AUDITOR_EXCLUSIVE) — examiner independence is enforced about the person.`}
    />
  );
}

/** Custom-role checkbox metas from the ACTIVE list. */
function customRoleMetas(customRoles: CustomRole[]): RoleMeta[] {
  return customRoles.map((c) => ({
    role: c.roleKey,
    label: c.displayName || c.roleKey,
    blurb: `Tenant-authored — binds ${c.permissionKeys.length} matrix key${c.permissionKeys.length === 1 ? '' : 's'}.`,
    assignable: true,
  }));
}

// ---------------------------------------------------------------------------------------------
// Invite
// ---------------------------------------------------------------------------------------------

interface InviteForm {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  spokeId?: string;
}

function InviteModal({
  open,
  spokes,
  spokesUnavailable,
  customRoles,
  onClose,
  onCreated,
}: {
  open: boolean;
  spokes: Spoke[];
  spokesUnavailable: boolean;
  customRoles: CustomRole[];
  onClose: () => void;
  onCreated: (view: UamInviteView) => void;
}) {
  const [form] = Form.useForm<InviteForm>();
  const watchedRoles = Form.useWatch('roles', form);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  // Fresh alert on each open (the Form fields are reset by destroyOnClose).
  useEffect(() => {
    if (open) setAlert(null);
  }, [open]);

  const submit = useCallback(async () => {
    let values: InviteForm;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setAlert(null);
    setSubmitting(true);
    try {
      const view = await inviteUser({
        username: values.username.trim(),
        email: values.email.trim(),
        firstName: values.firstName?.trim() || undefined,
        lastName: values.lastName?.trim() || undefined,
        roles: values.roles,
        spokeId: values.spokeId || undefined,
      });
      form.resetFields();
      onCreated(view);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 409 && n.code === 'USERNAME_TAKEN') {
        form.setFields([{ name: 'username', errors: ['That username is already taken — choose another.'] }]);
      } else if (n.code === 'UAM_ROLE_FORBIDDEN') {
        setAlert(
          'One of the selected roles can’t be assigned through user management (platform_admin is never assignable here).',
        );
      } else if (n.code === 'UAM_ROLE_UNKNOWN') {
        setAlert('One or more selected roles aren’t recognised by this hub. Refresh the page and try again.');
      } else if (n.code === 'UAM_AUDITOR_EXCLUSIVE') {
        setAlert(
          'Auditor is exclusive: it can’t be combined with roles that carry mutating permission keys — examiner independence is enforced about the person. Drop auditor, or drop the mutating roles.',
        );
      } else if (n.code === 'UAM_SPOKE_UNKNOWN') {
        form.setFields([{ name: 'spokeId', errors: ['That spoke no longer exists — clear it or pick another.'] }]);
      } else if (isNotAvailable(n)) {
        setAlert('The user-management API is not live yet (Track A pending). Your invite was not created.');
      } else if (n.httpStatus === 409) {
        setAlert(n.message || 'That request conflicts with an existing one.');
      } else {
        setAlert(n.message);
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, onCreated]);

  const options = spokeOptions(spokes);

  return (
    <Modal
      title="Invite a user"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Send invite"
      confirmLoading={submitting}
      width={560}
      destroyOnClose
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 4 }}>
        Creates the user in this hub's realm and returns a one-time temporary password (shown once, on
        the next screen). The user sets their own password at first sign-in.
      </Paragraph>
      {alert && (
        <Alert
          type="error"
          showIcon
          message={alert}
          style={{ marginBottom: 12 }}
          closable
          onClose={() => setAlert(null)}
        />
      )}
      <Form form={form} layout="vertical" requiredMark="optional" preserve={false}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item
            name="username"
            label="Username"
            style={{ flex: 1 }}
            rules={[
              { required: true, message: 'A username is required.' },
              { min: 3, max: 64, message: '3–64 characters.' },
            ]}
          >
            <Input placeholder="e.g. asha.rao" autoFocus autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            style={{ flex: 1 }}
            rules={[{ required: true, type: 'email', message: 'A valid email is required.' }]}
          >
            <Input placeholder="asha.rao@bank.example" autoComplete="off" />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="firstName" label="First name" style={{ flex: 1 }}>
            <Input placeholder="Asha" autoComplete="off" />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" style={{ flex: 1 }}>
            <Input placeholder="Rao" autoComplete="off" />
          </Form.Item>
        </div>

        <Form.Item
          name="roles"
          label="Roles"
          rules={[{ required: true, message: 'Select at least one role.' }]}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {ASSIGNABLE_ROLES.map((o) => (
                <RoleOption key={o.role} meta={o} />
              ))}
              {customRoles.length > 0 && (
                <>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Custom roles (this hub)
                  </Text>
                  {customRoleMetas(customRoles).map((o) => (
                    <RoleOption key={o.role} meta={o} custom />
                  ))}
                </>
              )}
            </Space>
          </Checkbox.Group>
        </Form.Item>
        <AuditorExclusivityHint roles={watchedRoles ?? []} customRoles={customRoles} />

        <Form.Item
          name="spokeId"
          label="Spoke"
          tooltip="Optional. Leave blank for hub scope — the user then sees all spokes' data, subject to their roles."
          extra={
            spokesUnavailable && spokes.length === 0
              ? 'The spoke list is unavailable, so only hub scope is offered right now.'
              : undefined
          }
        >
          <Select
            allowClear
            placeholder="Hub scope (no spoke)"
            options={options}
            optionFilterProp="label"
            showSearch
            disabled={spokesUnavailable && spokes.length === 0}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// One-time temporary-password reveal — dismiss-once, copy-to-clipboard, never logged/toasted.
// ---------------------------------------------------------------------------------------------

function TempPasswordModal({ view, onClose }: { view: UamInviteView; onClose: () => void }) {
  const { message } = App.useApp();
  const [copied, setCopied] = useState(false);
  const pwd = view.temporaryPassword;
  const username = view.user.username;

  const copy = useCallback(async () => {
    try {
      if (!pwd) throw new Error('no password');
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(pwd);
      setCopied(true);
      // NB: the toast never carries the password itself — only that a copy happened.
      message.success('Temporary password copied to the clipboard.');
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      message.error('Copy failed — select the password text and copy it manually.');
    }
  }, [pwd, message]);

  // Rare: an identical same-key retry replays the invite WITHOUT the one-time password (the server
  // never persists a live credential — see the idempotency redaction). The account exists; issue a
  // fresh temporary password via a re-invite / reset rather than showing an empty secret.
  if (!pwd) {
    return (
      <Modal
        title={
          <span>
            <KeyOutlined style={{ marginRight: 8, color: 'var(--ol-attention)' }} />
            User already created
          </span>
        }
        open
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="The temporary password was already issued"
          description={
            <span>
              <strong>{username}</strong> exists in this hub, but the one-time password was only shown
              on the original invite and is never stored. If it was lost, re-issue access to generate a
              new temporary password.
            </span>
          }
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <span>
          <KeyOutlined style={{ marginRight: 8, color: 'var(--ol-attention)' }} />
          User created — copy the temporary password
        </span>
      }
      open
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <Button type="primary" onClick={onClose}>
          I&rsquo;ve saved it — close
        </Button>
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 14 }}
        message="Shown once — it will never be shown again"
        description={
          <span>
            Copy this temporary password now and share it with <strong>{username}</strong> over a secure
            channel. It is not stored anywhere and cannot be retrieved later. If you lose it, you&rsquo;ll
            need to re-issue access.
          </span>
        }
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          border: '1px solid var(--ol-border-subtle)',
          borderRadius: 8,
          background: 'var(--ol-bg-subtle)',
        }}
      >
        <Text
          className="ol-mono"
          style={{ flex: 1, fontSize: 15, letterSpacing: 0.5, wordBreak: 'break-all', userSelect: 'all' }}
        >
          {pwd}
        </Text>
        <Button icon={copied ? <CheckOutlined /> : <CopyOutlined />} onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <Paragraph type="secondary" style={{ fontSize: 12, margin: '12px 0 0' }}>
        <strong>{username}</strong> will be prompted to set a new password at first sign-in.
      </Paragraph>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Edit roles (replace-set)
// ---------------------------------------------------------------------------------------------

function mapMutationError(n: NormalizedApiError): string {
  switch (n.code) {
    case 'UAM_ROLE_FORBIDDEN':
      return 'That role can’t be assigned here (platform_admin is never assignable through user management).';
    case 'UAM_ROLE_UNKNOWN':
      return 'One or more selected roles aren’t recognised by this hub.';
    case 'UAM_SPOKE_UNKNOWN':
      return 'That spoke no longer exists — pick another, or clear to hub scope.';
    case 'UAM_SELF_FORBIDDEN':
      return 'You can’t change your own roles, spoke, or status.';
    case 'LAST_HUB_ADMIN_PROTECTED':
      return 'This user is the last enabled hub admin. Removing hub_admin (or disabling them) would leave the hub without an administrator, so the server refuses. Promote another hub admin first, then retry.';
    case 'UAM_AUDITOR_EXCLUSIVE':
      return 'Auditor is exclusive: a person holding auditor may hold no other role that carries a mutating permission key — examiner independence is enforced about the person, not just the role. Drop auditor, or drop the mutating roles.';
    case 'UAM_USER_NOT_FOUND':
      return 'That user no longer exists in this hub (it may have been removed in Keycloak). Refresh the list.';
    default:
      return isNotAvailable(n) ? 'The user-management API is not live yet (Track A pending).' : n.message;
  }
}

function EditRolesModal({
  target,
  customRoles,
  onClose,
  onUpdated,
}: {
  target: UamUserView;
  customRoles: CustomRole[];
  onClose: () => void;
  onUpdated: (u: UamUserView) => void;
}) {
  const [roles, setRoles] = useState<string[]>(target.roles);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  // Catalogue, then this hub's ACTIVE custom roles, then any role the user already holds that is
  // neither (e.g. a RETIRED custom role) — kept selectable so a save never silently drops it.
  const { catalogueOpts, customOpts, retainedOpts } = useMemo(() => {
    const customMetas = customRoleMetas(customRoles);
    const known = new Set([...CATALOGUE_ROLE_NAMES, ...customMetas.map((m) => m.role)]);
    const retained = target.roles
      .filter((r) => !known.has(r) && r !== 'platform_admin')
      .map(
        (r): RoleMeta => ({
          role: r,
          label: r,
          blurb: isCustomRoleName(r)
            ? 'Custom role no longer ACTIVE — retained as-is (it currently grants nothing).'
            : 'Non-catalogue role — retained as-is.',
          assignable: true,
        }),
      );
    return { catalogueOpts: [...ASSIGNABLE_ROLES], customOpts: customMetas, retainedOpts: retained };
  }, [target.roles, customRoles]);

  // Proactive last-admin heads-up: the client can't COUNT enabled hub admins (that fact is the
  // server's), so this warns only that the guardrail exists when hub_admin is being removed.
  const droppingHubAdmin = target.roles.includes('hub_admin') && !roles.includes('hub_admin');

  const submit = useCallback(async () => {
    if (roles.length === 0) {
      setAlert('Select at least one role.');
      return;
    }
    setAlert(null);
    setSubmitting(true);
    try {
      const view = await replaceUserRoles(target.userId, { roles });
      onUpdated(view);
    } catch (e) {
      setAlert(mapMutationError(normalizeApiError(e)));
    } finally {
      setSubmitting(false);
    }
  }, [roles, target.userId, onUpdated]);

  return (
    <Modal
      title={`Edit roles — ${target.username}`}
      open
      onCancel={onClose}
      onOk={submit}
      okText="Save roles"
      confirmLoading={submitting}
      width={520}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 4 }}>
        This replaces the user&rsquo;s entire role set — the roles you check here become their roles.
      </Paragraph>
      {alert && <Alert type="error" showIcon message={alert} style={{ marginBottom: 12 }} />}
      <Checkbox.Group value={roles} onChange={(v) => setRoles(v.map(String))} style={{ width: '100%' }}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {catalogueOpts.map((o) => (
            <RoleOption key={o.role} meta={o} />
          ))}
          {customOpts.length > 0 && (
            <>
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Custom roles (this hub)
              </Text>
              {customOpts.map((o) => (
                <RoleOption key={o.role} meta={o} custom />
              ))}
            </>
          )}
          {retainedOpts.map((o) => (
            <RoleOption key={o.role} meta={o} custom={isCustomRoleName(o.role)} />
          ))}
        </Space>
      </Checkbox.Group>
      {droppingHubAdmin && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 10 }}
          message="Removing hub admin"
          description="If this is the hub's last enabled hub admin, the server will refuse (LAST_HUB_ADMIN_PROTECTED) — a hub can never lock itself out of administration. Promote another hub admin first if that's the case."
        />
      )}
      <AuditorExclusivityHint roles={roles} customRoles={customRoles} />
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Change spoke (bind one, or clear to hub scope)
// ---------------------------------------------------------------------------------------------

function ChangeSpokeModal({
  target,
  spokes,
  spokesUnavailable,
  onClose,
  onUpdated,
}: {
  target: UamUserView;
  spokes: Spoke[];
  spokesUnavailable: boolean;
  onClose: () => void;
  onUpdated: (u: UamUserView) => void;
}) {
  const [spokeId, setSpokeId] = useState<string | null>(target.spokeId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  const options = spokeOptions(spokes, target.spokeId);

  const submit = useCallback(async () => {
    setAlert(null);
    setSubmitting(true);
    try {
      // Send null explicitly to clear to hub scope (the request field is required + nullable).
      const view = await setUserSpoke(target.userId, { spokeId: spokeId ?? null });
      onUpdated(view);
    } catch (e) {
      setAlert(mapMutationError(normalizeApiError(e)));
    } finally {
      setSubmitting(false);
    }
  }, [spokeId, target.userId, onUpdated]);

  return (
    <Modal
      title={`Change spoke — ${target.username}`}
      open
      onCancel={onClose}
      onOk={submit}
      okText="Save spoke"
      confirmLoading={submitting}
      width={480}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 4 }}>
        Bind this user to a single spoke, or clear the binding to return them to hub scope (they then
        see all spokes&rsquo; data, subject to their roles). A user can hold at most one spoke.
      </Paragraph>
      {alert && <Alert type="error" showIcon message={alert} style={{ marginBottom: 12 }} />}
      <Select
        allowClear
        showSearch
        style={{ width: '100%' }}
        placeholder="Hub scope (no spoke)"
        value={spokeId ?? undefined}
        onChange={(v) => setSpokeId(v ?? null)}
        options={options}
        optionFilterProp="label"
      />
      {spokesUnavailable && spokes.length === 0 && (
        <Paragraph type="secondary" style={{ fontSize: 11.5, marginTop: 8 }}>
          The spoke list is unavailable, so you can only clear to hub scope right now.
        </Paragraph>
      )}
    </Modal>
  );
}
